/**
 * Home2 Hero Component
 * Hero section with headline, stats, and primary CTA.
 * 
 * @module components/home2/Home2Hero
 */

import React from 'react';
import { Button } from '../ui/button';
import { BarChart3, Clock, TrendingUp, Brain, ArrowRight, ChevronDown } from 'lucide-react';
import { HERO_CONTENT, PROOF_STATS } from '../../data/home2Content';

const iconMap = {
  chart: BarChart3,
  clock: Clock,
  trending: TrendingUp,
  brain: Brain,
};

const Home2Hero = ({ onPrimaryCTA, onSecondaryCTA }) => {
  return (
    <section className="container mx-auto px-4 py-16 md:py-24">
      <div className="max-w-4xl mx-auto text-center">
        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
          {HERO_CONTENT.headline}
        </h1>
        
        {/* Subheadline */}
        <p className="text-base md:text-lg text-slate-600 mb-4 leading-relaxed max-w-3xl mx-auto">
          {HERO_CONTENT.subheadline}
        </p>
        
        {/* Solution Statement */}
        <p className="text-xl md:text-2xl font-semibold text-indigo-600 mb-8">
          {HERO_CONTENT.solution}
        </p>
        
        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Button 
            size="lg" 
            onClick={onPrimaryCTA}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg"
          >
            {HERO_CONTENT.primaryCTA}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            onClick={onSecondaryCTA}
            className="border-slate-300 text-slate-700 px-8 py-6 text-lg"
          >
            {HERO_CONTENT.secondaryCTA}
            <ChevronDown className="ml-2 h-5 w-5" />
          </Button>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {PROOF_STATS.map((stat, index) => {
            const Icon = iconMap[stat.icon];
            return (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-center mb-2">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Icon className="h-5 w-5 text-indigo-600" />
                  </div>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-indigo-600 mb-1">
                  {stat.value}
                </div>
                <p className="text-xs md:text-sm text-slate-600 leading-snug">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Home2Hero;
